import { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
    Table,
    Card,
    Spin,
    Button,
    Input,
    Select,
    Row,
    Col,
    Modal,
} from "antd";
import { AuthContext } from "../../context/AuthContext";
import ViewEmpSalary from "../../components/ViewEmpSalary";
import { useNavigate } from "react-router-dom";
import EditOneEmployee from "../../components/EditOneEmployee";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FilePdfOutlined, FileExcelOutlined } from "@ant-design/icons";

const { Option } = Select;

export default function FullSettlement() {
    const { authData, backUrl } = useContext(AuthContext);
    const navigate = useNavigate();

    const [showSalary, setShowSalary] = useState(false)

    const [showEmp, setShowEmp] = useState(false)
    const [selectedId, setSelectedId] = useState(0)

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);

    // Cascading filters
    const [organizations, setOrganizations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    // Search filters
    const [searchCode, setSearchCode] = useState("");
    const [searchName, setSearchName] = useState("");

    // Salary view state
    const [showCalculator, setShowCalculator] = useState(false);
    const [particular, setParticular] = useState(null);

    const fetchEmployees = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${backUrl}/api/emp/employees/?status=Resigned`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setEmployees(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching employees:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganizations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching organizations:", error);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`);
            setDepartments(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching departments:", error);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`);
            setDesignations(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching designations:", error);
        }
    };

    useEffect(() => {
        fetchEmployees();
        fetchOrganizations();
    }, []);

    // Filter employees based on code, name, and cascading filters
    const filteredEmployees = employees.filter((emp) => {
        const matchesCode = emp.user?.username?.toLowerCase().includes(searchCode.toLowerCase());
        const matchesName = emp.user?.first_name?.toLowerCase().includes(searchName.toLowerCase());
        const matchesOrg = selectedOrg ? emp.organisation === selectedOrg : true;
        const matchesDept = selectedDept ? emp.department === selectedDept : true;
        const matchesDesig = selectedDesig ? emp.designation === selectedDesig : true;
        return matchesCode && matchesName && matchesOrg && matchesDept && matchesDesig;
    });

    const handleSalaryClick = (record) => {
        setParticular(record);
        setShowCalculator(true);
    };

    const handleFullAndFinal = async (employeeId) => {
        try {
            setLoading(true);
            const res = await axios.post(
                `${backUrl}/api/payroll/settlements/create/`,
                { employee_id: employeeId },
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            toast.success("Full & Final settlement process triggered successfully.");
        } catch (error) {
            console.error("Error triggering settlement:", error);

            // Show detailed backend error if available
            if (error.response && error.response.data && error.response.data.error) {
                toast.error(` ${error.response.data.error}`);
            } else {
                toast.error(" Failed to trigger Full & Final settlement. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const formatRow = (row) => ({
        "Employee Code": row.user?.username || "-",
        "First Name": row.user?.first_name || "-",
        "Last Name": row.user?.last_name || "-",
        "Initial": row.initial || "-",
        "Department": row.department_name || "-",
        "Designation": row.designation_name || "-",
        "Organisation": row.organisation_name || "-",
        "Location": row.location || "-",
        "Status": row.status || "-",
        "Date of Joining": row.date_of_joining || "-",
        "Date of Birth": row.date_of_birth || "-",
        "Gender": row.gender || "-",
        "Marital Status": row.marital_status || "-",
        "Father Name": row.father_name || "-",
        "Father Profession": row.father_profession || "-",
        "Mother Name": row.mother_name || "-",
        "Mother Profession": row.mother_profession || "-",
        "Spouse Name": row.spouse_name || "-",
        "Spouse DOB": row.spouse_dob || "-",
        "Wedding Date": row.wedding_date || "-",
        "Blood Group": row.blood_group || "-",
        "Religion": row.religion || "-",
        "Mother Tongue": row.mother_tongue || "-",
        "Email": row.user?.email || "-",
        "Mobile No": row.mobile_number || "-",
        "Emergency Contact": row.emergency_contact_number || "-",
        "Emergency Person": row.emergency_contact_person || "-",
        "PAN": row.pan_card_number || "-",
        "Aadhaar": row.aadhaar_card_number || "-",
        "UAN": row.uan_number || "-",
        "ESIC": row.esic_ip_number || "-",
        "Bank Account": row.bank_account_number || "-",
        "Branch Name": row.branch_name || "-",
        "IFSC Code": row.ifsc_code || "-",
        "Address": row.address_for_communication || "-",
        "Area": row.area || "-",
        "City": row.city || "-",
        "State": row.state || "-",
        "Country": row.country || "-",
        "Pincode": row.pincode || "-",
        "Permanent Address": row.permanent_address_for_communication || "-",
        "Permanent City": row.permanent_city || "-",
        "Permanent State": row.permanent_state || "-",
        "Permanent Country": row.permanent_country || "-",
        "Permanent Pincode": row.permanent_pincode || "-",
        "Present Address": row.present_address ? "Yes" : "-",
        "Height": row.height || "-",
        "Weight": row.weight || "-",
        "Probation Period (Months)": row.probation_period_months || "-",
        "Reporting To": row.reporting_to_name || "-",
        "Taken Casual Leave": row.taken_casual_leave || "-",
        "Taken Earned Leave": row.taken_earned_leave || "-",
        "Taken Sick Leave": row.taken_sick_leave || "-",
        "Taken LOP": row.taken_lop_leave || "-",
    });

    const handleExportExcel = () => {
        const XLSX = require("xlsx");

        const formatted = filteredEmployees.map(formatRow);

        const ws = XLSX.utils.json_to_sheet(formatted);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Employees");

        XLSX.writeFile(wb, "Employees.xlsx");
    };

    // 🔷 PDF Export
    const handleExportPDF = () => {
        const jsPDF = require("jspdf");
        const autoTable = require("jspdf-autotable");

        const doc = new jsPDF("l", "pt", "a3");

        const formatted = filteredEmployees.map(formatRow);
        const columns = Object.keys(formatRow(filteredEmployees[0] || {}));

        autoTable.default(doc, {
            head: [columns],
            body: formatted.map((row) => Object.values(row)),
            startY: 20,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [14, 73, 123] },
        });

        doc.save("Employees.pdf");
    };


    const renderValue = (value) => {
        if (value === null || value === undefined || value === "") return "-";
        return value;
    };

    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["user", "username"],
            key: "user.username",
            fixed: "left",
            render: (text, record) => (
                <a
                    onClick={() => {
                        setSelectedId(record.id)
                        setShowEmp(true)
                    }}
                    style={{ cursor: "pointer" }}
                >
                    {renderValue(text)}
                </a>
            ),
        },
        { title: "First Name", dataIndex: ["user", "first_name"], render: renderValue },
        { title: "Last Name", dataIndex: ["user", "last_name"], render: renderValue },
        { title: "Initial", dataIndex: "initial", render: renderValue },
        { title: "Department", dataIndex: "department_name", render: renderValue },
        { title: "Designation", dataIndex: "designation_name", render: renderValue },
        { title: "Organisation", dataIndex: "organisation_name", render: renderValue },
        { title: "Location", dataIndex: "location", render: renderValue },
        { title: "Status", dataIndex: "status", render: renderValue },
        { title: "Taken Casual Leave", dataIndex: "taken_casual_leave", render: renderValue },
        { title: "Taken Earned Leave", dataIndex: "taken_earned_leave", render: renderValue },
        { title: "Taken Sick Leave", dataIndex: "taken_sick_leave", render: renderValue },
        { title: "Taken LOP", dataIndex: "taken_lop_leave", render: renderValue },

        // {
        //     title: "Salary Details",
        //     key: "salary",
        //     render: (_, record) => (
        //         <Button type="primary" onClick={() => { setShowSalary(true) }}>
        //             View Salary
        //         </Button>
        //     ),
        // },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Button type="primary" danger onClick={() => handleFullAndFinal(record.id)}>
                    Full & Final Settlement
                </Button>
            ),
        },
    ];


    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />

            {
                showEmp ? (
                    <>
                        {
                            selectedId !== 0 && (
                                // <EditEmployeeProfile employeeid={selectedId} />
                                <>
                                    <EditOneEmployee employeeId={selectedId} />
                                </>
                            )
                        }
                    </>
                ) : (
                    <>
                        {showCalculator ? (
                            <>
                                <Button
                                    onClick={() => {
                                        setShowCalculator(false);
                                        setParticular(null);
                                    }}
                                    style={{ marginBottom: "10px" }}
                                >
                                    ← Back
                                </Button>
                                <ViewEmpSalary amount={particular.offered_salary} cid={particular.id} />
                            </>
                        ) : (
                            <>
                                {/* 🔹 Filter Section */}
                                <Card
                                    title=""
                                    bordered
                                    style={{
                                        marginBottom: 20,
                                        borderRadius: 8,
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                    }}
                                >
                                    <Row style={{ marginBottom: 10 }}>
                                        <Col>
                                            <Button
                                                type="primary"
                                                icon={<FileExcelOutlined />}
                                                onClick={handleExportExcel}
                                                style={{ marginRight: 10, background: 'green', color: 'white' }}
                                            >
                                            </Button>


                                        </Col>
                                    </Row>
                                    <Row gutter={16} style={{ marginBottom: 10 }}>
                                        <Col span={6}>
                                            <Input
                                                allowClear
                                                placeholder="Search Employee Code"
                                                value={searchCode}
                                                onChange={(e) => setSearchCode(e.target.value)}
                                            />
                                        </Col>
                                        <Col span={6}>
                                            <Input
                                                allowClear
                                                placeholder="Search Employee Name"
                                                value={searchName}
                                                onChange={(e) => setSearchName(e.target.value)}
                                            />
                                        </Col>
                                        <Col span={6}>
                                            <Select
                                                allowClear
                                                placeholder="Select Organization"
                                                style={{ width: "100%" }}
                                                value={selectedOrg}
                                                onChange={(val) => {
                                                    setSelectedOrg(val);
                                                    setSelectedDept(null);
                                                    setSelectedDesig(null);
                                                    setDepartments([]);
                                                    setDesignations([]);
                                                    if (val) fetchDepartments(val);
                                                }}
                                            >
                                                {organizations.map((o) => (
                                                    <Option key={o.id} value={o.id}>
                                                        {o.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Col>
                                        <Col span={6}>
                                            <Select
                                                allowClear
                                                placeholder="Select Department"
                                                style={{ width: "100%" }}
                                                value={selectedDept}
                                                onChange={(val) => {
                                                    setSelectedDept(val);
                                                    setSelectedDesig(null);
                                                    setDesignations([]);
                                                    if (val) fetchDesignations(val);
                                                }}
                                                disabled={!selectedOrg}
                                            >
                                                {departments.map((d) => (
                                                    <Option key={d.id} value={d.id}>
                                                        {d.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={6}>
                                            <Select
                                                allowClear
                                                placeholder="Select Designation"
                                                style={{ width: "100%" }}
                                                value={selectedDesig}
                                                onChange={(val) => setSelectedDesig(val)}
                                                disabled={!selectedDept}
                                            >
                                                {designations.map((d) => (
                                                    <Option key={d.id} value={d.id}>
                                                        {d.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Col>
                                        <Col span={6}>
                                            <Button
                                                onClick={() => {
                                                    setSearchCode("");
                                                    setSearchName("");
                                                    setSelectedOrg(null);
                                                    setSelectedDept(null);
                                                    setSelectedDesig(null);
                                                    setDepartments([]);
                                                    setDesignations([]);
                                                }}
                                            >
                                                Reset
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* 🔹 Table Section */}
                                {loading ? (
                                    <div style={{ textAlign: "center", padding: "40px" }}>
                                        <Spin size="large" />
                                    </div>
                                ) : (
                                    <Table
                                        rowKey="id"
                                        dataSource={filteredEmployees}
                                        columns={columns}
                                        bordered
                                        pagination={{ pageSize: 8 }}
                                        scroll={{ x: 3000 }}
                                        components={{
                                            header: {
                                                cell: (props) => (
                                                    <th
                                                        {...props}
                                                        style={{
                                                            backgroundColor: "#0e497b",
                                                            color: "white",
                                                        }}
                                                    >
                                                        {props.children}
                                                    </th>
                                                ),
                                            },
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </>
                )
            }

        </>
    );
}

